<template>
  <div class="page-bg" :class="`page-bg--${scrim}`" :style="style" aria-hidden="true"></div>
</template>

<script setup>
import { computed } from 'vue';

/*
 * Replaces HeroBackground for public pages.
 *
 * HeroBackground took no props: consumers set id="background" and overrode it
 * from *scoped* CSS, which worked only because Vue stamps the parent's scope
 * attribute onto a child's root element and an id outranks a class. Adding a
 * wrapper element to that template would have silently broken the background
 * on twelve pages. The image and scrim are real props here.
 */
const props = defineProps({
  image: {
    type: String,
    default: '',
  },
  /* `strong` for pages with text outside cards; `soft` where content sits on
   * opaque surfaces and the artwork should read. */
  scrim: {
    type: String,
    default: 'strong',
    validator: (v) => ['soft', 'strong'].includes(v),
  },
});

const style = computed(() => ({
  backgroundImage: props.image ? `url(${props.image})` : 'none',
}));
</script>

<style scoped>
.page-bg {
  position: fixed;
  inset: 0;
  z-index: var(--z-background);
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  /* 100dvh, not 100vh — the old value fought mobile browser chrome. */
  height: 100dvh;
  pointer-events: none;
}

.page-bg::after {
  content: '';
  position: absolute;
  inset: 0;
}

/*
 * A flat scrim at a real opacity. The previous stack let 20–28% of the
 * photograph through, so identical text measured 15:1 over dark regions and
 * 1.5:1 over bright ones — contrast depended on where the reader had
 * scrolled. The 1–2px backdrop-filter used to compensate was perceptually a
 * no-op that forced a compositing layer on elements up to 5000px tall.
 */
.page-bg--strong::after {
  background: linear-gradient(
    to bottom,
    var(--scrim-strong),
    rgba(8, 11, 16, 0.88)
  );
}

.page-bg--soft::after {
  background: linear-gradient(
    to bottom,
    var(--scrim-soft),
    rgba(8, 11, 16, 0.7)
  );
}
</style>
